// Step1BasicInfo.jsx - PART 1: Imports and Component Setup
import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Card from "./ui/Card";
import TextField from "./ui/TextField";
import SelectField from "./ui/SelectField";
import RadioGroup from "./ui/RadioGroup";
import DuplicateApplicationDialog from "./ui/DuplicateApplicationDialog";
import ErrorDialog from "./ui/ErrorDialogBox";
import { SRI_LANKA_DISTRICTS, BIRTH_DISTRICTS } from "./constants/districts";
import { OCCUPATIONS } from "./constants/occupations";
import { applyPassportService } from "../../services/passportApi";

export default function ApplicationForm({
  data = {},
  setField = () => { },
  onSubmit = () => { },
  onFileChange = () => { },
  submitting = false,
  onNextStep = () => { },
  lockedFields = [
    "surname",
    "otherNames",
    "birthDate",
    "gender",
    "emailAddress",
  ],
  isRenewal = false,
}) {
  const navigate = useNavigate();

  console.log("locked fields", lockedFields);

  const [files, setFiles] = useState({});
  const [posting, setPosting] = useState(false);
  const [customOccupation, setCustomOccupation] = useState("");
  const [touchedFields, setTouchedFields] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const [showDuplicateApplicationDialog, setShowDuplicateApplicationDialog] = useState(false);
  const [existingApplicationData, setExistingApplicationData] = useState(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);

  const isSubmitting = submitting || posting;

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  // Field length constraints
  const FIELD_MAX_LENGTHS = {
    surname: 50,
    otherNames: 50,
    placeOfBirth: 50,
    foreignNationality: 50,
    birthDistrict: 50,
    permanentAddressLine1: 100,
    permanentAddressLine2: 100,
    permanentAddressCity: 100,
    permanentAddressDistrict: 50,
    birthCertificateNo: 30,
    dualCitizenshipNo: 30,
    foreignPassportNo: 30,
    presentTravelDocNo: 30,
    nmrpNo: 30,
    occupation: 100,
    customOccupation: 100,
    emailAddress: 30,
    photoAcknowledgmentNo: 30,
    serviceType: 20,
    documentType: 50,
    gender: 20,
    fingerprintLocation: 50,
    paymentStatus: 20,
    paymentReference: 50,
  };

  const validateSLUDI = (sludiNumber) => {

    if (!sludiNumber) return "SLUDI Number is required";
    const trimmed = sludiNumber.trim();
    if (trimmed.length < 10) {
      return `SLUDI Number must be exactly 10 digits (currently ${trimmed.length})`;
    }
    const sludiRegex = /^\d{10}$/;
    if (!sludiRegex.test(trimmed)) {
      return "Invalid SLUDI format. Must be exactly 10 digits";
    }
    return "";
  };

  const validateDateOfBirth = (birthDate) => {
    if (!birthDate.year || !birthDate.month || !birthDate.day) {
      return "Date of Birth is required";
    }
    const year = parseInt(birthDate.year);
    const month = parseInt(birthDate.month);
    const day = parseInt(birthDate.day);
    const dob = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dob.getMonth() !== month - 1 || dob.getDate() !== day) {
      return "Invalid Date of Birth";
    }
    if (dob > today) {
      return "Date of Birth cannot be in the future";
    }
    return "";
  };

  const validateOccupation = (occupation, customOcc) => {
    if (!occupation) {
      return "Occupation is required";
    }
    if (occupation === "Other" && (!customOcc || customOcc.trim() === "")) {
      return "Please specify your occupation";
    }
    return "";
  };

  const validateEmail = (email) => {
    if (!email) return "Email Address is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const validateMobile = (mobile) => {
    if (!mobile) return "Mobile Number is required";
    const trimmed = mobile.trim();
    if (trimmed.length < 9) {
      return `Mobile number is too short. Must be at least 9 digits (currently ${trimmed.length})`;
    }
    const mobileWithZeroRegex = /^0\d{9}$/;
    const mobileWithoutZeroRegex = /^\d{9}$/;
    if (!mobileWithZeroRegex.test(trimmed) && !mobileWithoutZeroRegex.test(trimmed)) {
      return "Mobile number must be exactly 9 digits or 10 digits starting with 0 (e.g., 715189425 or 0715189425)";
    }
    return "";
  };

  const validateRequiredField = (value, fieldName) => {
    if (!value || (typeof value === "string" && value.trim() === "")) {
      return `${fieldName} is required`;
    }
    if (typeof value === "object" && value !== null) {
      const hasValue = Object.values(value).some((v) => v !== "" && v !== null);
      if (!hasValue) return `${fieldName} is required`;
    }
    return "";
  };

  const validateFieldLength = (value, fieldName, maxLength) => {
    if (!value) return "";
    const length = typeof value === "string" ? value.length : 0;
    if (length > maxLength) {
      return `${fieldName} exceeds maximum length of ${maxLength} characters (currently ${length} characters)`;
    }
    return "";
  };

  const validateField = (name, value) => {

    let error = "";
    switch (name) {
      case "sludiNumber":
        error = validateSLUDI(value);
        break;
      case "birthDate":
        error = validateDateOfBirth(value);
        break;
      case "gender":
        error = validateRequiredField(value, "Gender");
        break;
      case "surname":
        error = validateRequiredField(value, "Surname");
        if (!error) error = validateFieldLength(value, "Surname", FIELD_MAX_LENGTHS.surname);
        break;
      case "otherNames":
        error = validateRequiredField(value, "Other Names");
        if (!error) error = validateFieldLength(value, "Other Names", FIELD_MAX_LENGTHS.otherNames);
        break;
      case "permanentAddress.line1":
        error = validateRequiredField(value, "Address Line 1");
        if (!error) error = validateFieldLength(value, "Address Line 1", FIELD_MAX_LENGTHS.permanentAddressLine1);
        break;
      case "permanentAddress.line2":
        if (!error) error = validateFieldLength(value, "Address Line 2", FIELD_MAX_LENGTHS.permanentAddressLine2);
        break;
      case "permanentAddress.city":
        error = validateRequiredField(value, "City");
        if (!error) error = validateFieldLength(value, "City", FIELD_MAX_LENGTHS.permanentAddressCity);
        break;
      case "permanentAddress.district":
        error = validateRequiredField(value, "District");
        if (!error) error = validateFieldLength(value, "District", FIELD_MAX_LENGTHS.permanentAddressDistrict);
        break;
      case "birthCertificateNo":
        error = validateRequiredField(value, "Birth Certificate Number");
        if (!error) error = validateFieldLength(value, "Birth Certificate Number", FIELD_MAX_LENGTHS.birthCertificateNo);
        break;
      case "birthDistrict":
        error = validateRequiredField(value, "Birth District");
        break;
      case "placeOfBirth":
        error = validateRequiredField(value, "Place of Birth");
        if (!error) error = validateFieldLength(value, "Place of Birth", FIELD_MAX_LENGTHS.placeOfBirth);
        break;
      case "occupation":
        error = validateOccupation(value, customOccupation);
        break;
      case "mobileNumber":
        error = validateMobile(value);
        break;
      case "emailAddress":
        error = validateEmail(value);
        if (!error) error = validateFieldLength(value, "Email Address", FIELD_MAX_LENGTHS.emailAddress);
        break;
      case "dualCitizenshipNo":
        if (safeData.hasDualCitizenship) {
          error = validateRequiredField(value, "Dual Citizenship Number");
          if (!error) error = validateFieldLength(value, "Dual Citizenship Number", FIELD_MAX_LENGTHS.dualCitizenshipNo);
        }
        break;
      case "foreignNationality":
        if (safeData.hasDualCitizenship) {
          error = validateRequiredField(value, "Foreign Nationality");
          if (!error) error = validateFieldLength(value, "Foreign Nationality", FIELD_MAX_LENGTHS.foreignNationality);
        }
        break;
      case "foreignPassportNo":
        if (safeData.hasDualCitizenship) {
          error = validateRequiredField(value, "Foreign Passport Number");
          if (!error) error = validateFieldLength(value, "Foreign Passport Number", FIELD_MAX_LENGTHS.foreignPassportNo);
        }
        break;
      case "presentTravelDocNo":
        if (value) error = validateFieldLength(value, "Present Travel Document Number", FIELD_MAX_LENGTHS.presentTravelDocNo);
        break;
      case "nmrpNo":
        if (value) error = validateFieldLength(value, "NMRP Number", FIELD_MAX_LENGTHS.nmrpNo);
        break;
      case "photoAcknowledgmentNo":
        if (value) error = validateFieldLength(value, "Photo Acknowledgment Number", FIELD_MAX_LENGTHS.photoAcknowledgmentNo);
        break;
      default:
        break;
    }
    return error;
  };


  const handleFieldBlur = (name) => {
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
    let value;
    if (name === "birthDate") {
      value = safeData.birthDate;
    } else if (name.includes(".")) {
      const parts = name.split(".");
      value = safeData[parts[0]][parts[1]];
    } else {
      value = safeData[name];
    }
    const error = validateField(name, value);
    setFieldErrors((prev) => ({ ...prev, [name]: error }));
  };

  const sanitizeInput = {
    name: (value) => value.replace(/[^a-zA-Z\s'-]/g, ''),
    numeric: (value) => value.replace(/[^0-9]/g, ''),
    alphanumeric: (value) => value.replace(/[^a-zA-Z0-9]/g, ''),
    address: (value) => value.replace(/[^a-zA-Z0-9\s,.\-/#]/g, ''),
    email: (value) => value.replace(/[^a-zA-Z0-9@._\-+]/g, ''),
    phone: (value) => value.replace(/[^0-9]/g, ''),
    text: (value) => value.replace(/[<>{}[\]\\]/g, ''),
    occupation: (value) => value.replace(/[^a-zA-Z\s\-]/g, ''),
  };

  const handleFieldChangeWithValidation = (name, value) => {
    let sanitizedValue = value;
    switch (name) {
      case "sludiNumber":
        sanitizedValue = sanitizeInput.numeric(value);
        if (sanitizedValue.length > 10) {
          sanitizedValue = sanitizedValue.slice(0, 10);
        }
        break;
      case "surname":
        sanitizedValue = sanitizeInput.name(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.surname) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.surname);
        break;
      case "otherNames":
        sanitizedValue = sanitizeInput.name(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.otherNames) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.otherNames);
        break;
      case "placeOfBirth":
        sanitizedValue = sanitizeInput.name(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.placeOfBirth) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.placeOfBirth);
        break;
      case "foreignNationality":
        sanitizedValue = sanitizeInput.name(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.foreignNationality) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.foreignNationality);
        break;
      case "permanentAddress.line1":
        sanitizedValue = sanitizeInput.address(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.permanentAddressLine1) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.permanentAddressLine1);
        break;
      case "permanentAddress.line2":
        sanitizedValue = sanitizeInput.address(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.permanentAddressLine2) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.permanentAddressLine2);
        break;
      case "permanentAddress.city":
        sanitizedValue = sanitizeInput.address(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.permanentAddressCity) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.permanentAddressCity);
        break;
      case "birthCertificateNo":
        sanitizedValue = sanitizeInput.alphanumeric(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.birthCertificateNo) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.birthCertificateNo);
        break;
      case "dualCitizenshipNo":
        sanitizedValue = sanitizeInput.alphanumeric(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.dualCitizenshipNo) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.dualCitizenshipNo);
        break;
      case "foreignPassportNo":
        sanitizedValue = sanitizeInput.alphanumeric(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.foreignPassportNo) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.foreignPassportNo);
        break;
      case "presentTravelDocNo":
        sanitizedValue = sanitizeInput.alphanumeric(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.presentTravelDocNo) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.presentTravelDocNo);
        break;
      case "nmrpNo":
        sanitizedValue = sanitizeInput.alphanumeric(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.nmrpNo) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.nmrpNo);
        break;
      case "photoAcknowledgmentNo":
        sanitizedValue = sanitizeInput.alphanumeric(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.photoAcknowledgmentNo) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.photoAcknowledgmentNo);
        break;
      case "mobileNumber":
        sanitizedValue = sanitizeInput.phone(value);
        if (sanitizedValue.length > 10) {
          sanitizedValue = sanitizedValue.slice(0, 10);
        }
        break;
      case "emailAddress":
        sanitizedValue = sanitizeInput.email(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.emailAddress) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.emailAddress);
        break;
      case "serviceType":
        sanitizedValue = sanitizeInput.text(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.serviceType) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.serviceType);
        break;
      case "documentType":
        sanitizedValue = sanitizeInput.text(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.documentType) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.documentType);
        break;
      case "gender":
        sanitizedValue = sanitizeInput.text(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.gender) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.gender);
        break;
      case "birthDistrict":
        sanitizedValue = sanitizeInput.name(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.birthDistrict) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.birthDistrict);
        break;
      case "fingerprintLocation":
        sanitizedValue = sanitizeInput.text(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.fingerprintLocation) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.fingerprintLocation);
        break;
      case "paymentStatus":
        sanitizedValue = sanitizeInput.text(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.paymentStatus) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.paymentStatus);
        break;
      case "paymentReference":
        sanitizedValue = sanitizeInput.text(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.paymentReference) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.paymentReference);
        break;
      case "permanentAddress.district":
        sanitizedValue = sanitizeInput.address(value);
        if (sanitizedValue.length > FIELD_MAX_LENGTHS.permanentAddressDistrict) sanitizedValue = sanitizedValue.slice(0, FIELD_MAX_LENGTHS.permanentAddressDistrict);
        break;
      case "occupation":
        break;
      default:
        sanitizedValue = sanitizeInput.text(value);
        if (sanitizedValue.length > 255) sanitizedValue = sanitizedValue.slice(0, 255);
        break;
    }
    setFieldGuarded(name, sanitizedValue);
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, sanitizedValue);
    setFieldErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleCustomOccupationChange = (name, value) => {
    let sanitized = sanitizeInput.occupation(value);
    if (sanitized.length > FIELD_MAX_LENGTHS.customOccupation) {
      sanitized = sanitized.slice(0, FIELD_MAX_LENGTHS.customOccupation);
    }
    setCustomOccupation(sanitized);
    setTouchedFields(prev => ({ ...prev, occupation: true }));
    const error = validateOccupation(safeData.occupation, sanitized);
    setFieldErrors(prev => ({ ...prev, occupation: error }));
  };

  const validateAllFields = () => {
    const errors = [];
    const newFieldErrors = {};
    const requiredFields = [
      { name: "sludiNumber", value: safeData.sludiNumber, validator: validateSLUDI },
      { name: "birthDate", value: safeData.birthDate, validator: validateDateOfBirth },
      { name: "gender", value: safeData.gender, validator: (v) => validateRequiredField(v, "Gender") },
      { name: "surname", value: safeData.surname, validator: (v) => validateRequiredField(v, "Surname") },
      { name: "otherNames", value: safeData.otherNames, validator: (v) => validateRequiredField(v, "Other Names") },
      { name: "permanentAddress.line1", value: safeData.permanentAddress.line1, validator: (v) => validateRequiredField(v, "Address Line 1") },
      { name: "permanentAddress.city", value: safeData.permanentAddress.city, validator: (v) => validateRequiredField(v, "City") },
      { name: "permanentAddress.district", value: safeData.permanentAddress.district, validator: (v) => validateRequiredField(v, "District") },
      { name: "birthCertificateNo", value: safeData.birthCertificateNo, validator: (v) => validateRequiredField(v, "Birth Certificate Number") },
      { name: "birthDistrict", value: safeData.birthDistrict, validator: (v) => validateRequiredField(v, "Birth District") },
      { name: "placeOfBirth", value: safeData.placeOfBirth, validator: (v) => validateRequiredField(v, "Place of Birth") },
      { name: "occupation", value: safeData.occupation, validator: (v) => validateOccupation(v, customOccupation) },
      { name: "mobileNumber", value: safeData.mobileNumber, validator: validateMobile },
      { name: "emailAddress", value: safeData.emailAddress, validator: validateEmail },
    ];

    if (safeData.hasDualCitizenship) {
      requiredFields.push(
        { name: "dualCitizenshipNo", value: safeData.dualCitizenshipNo, validator: (v) => validateRequiredField(v, "Dual Citizenship Number") },
        { name: "foreignNationality", value: safeData.foreignNationality, validator: (v) => validateRequiredField(v, "Foreign Nationality") },
        { name: "foreignPassportNo", value: safeData.foreignPassportNo, validator: (v) => validateRequiredField(v, "Foreign Passport Number") }
      );
    }

    requiredFields.forEach(({ name, value, validator }) => {
      const error = validator(value);
      if (error) {
        errors.push(error);
        newFieldErrors[name] = error;
      }
    });

    if (!files.birthCertFront && !safeData.birthCertFront) {
      errors.push("Birth Certificate (Front) is required");
      newFieldErrors.birthCertFront = "Required";
    } else if (files.birthCertFront && files.birthCertFront.size > 1048576) {
      errors.push("Birth Certificate must be less than 1MB");
      newFieldErrors.birthCertFront = "File size must be less than 1MB";
    }

    setFieldErrors(newFieldErrors);
    return errors;
  };

  const bytes = (n) => {
    if (n == null) return "";
    const u = ["B", "KB", "MB", "GB"];
    let i = 0;
    let x = n;
    while (x >= 1024 && i < u.length - 1) {
      x /= 1024;
      i++;
    }
    return `${x.toFixed(x < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
  };

  const uploadGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "1rem",
  };

  const uploadTileStyle = (disabled, hasError) => ({
    position: "relative",
    border: `2px dashed ${hasError ? "rgba(220, 38, 38, 0.5)" : "rgba(30, 64, 175, 0.35)"}`,
    borderRadius: 16,
    padding: "18px 16px",
    background: hasError
      ? "linear-gradient(180deg, rgba(254,242,242,0.6), rgba(254,226,226,0.35))"
      : "linear-gradient(180deg, rgba(239,246,255,0.6), rgba(219,234,254,0.35))",
    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.5), 0 6px 18px ${hasError ? "rgba(220, 38, 38, 0.08)" : "rgba(30, 64, 175, 0.08)"
      }`,
    transition: "transform .15s ease, box-shadow .15s ease, border-color .15s",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    fontFamily: "'Barlow', sans-serif",
  });

  const onTileHover = (e, hasError) => {
    if (e.currentTarget.style.cursor === "not-allowed") return;
    if (hasError) return;
    e.currentTarget.style.transform = "translateY(-2px)";
    e.currentTarget.style.boxShadow = "inset 0 1px 0 rgba(255,255,255,0.6), 0 10px 24px rgba(30, 64, 175, 0.12)";
    e.currentTarget.style.borderColor = "rgba(30, 64, 175, 0.65)";
  };

  const onTileLeave = (e, hasError) => {
    if (e.currentTarget.style.cursor === "not-allowed") return;
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = `inset 0 1px 0 rgba(255,255,255,0.5), 0 6px 18px ${hasError ? "rgba(220, 38, 38, 0.08)" : "rgba(30, 64, 175, 0.08)"
      }`;
    e.currentTarget.style.borderColor = hasError ? "rgba(220, 38, 38, 0.5)" : "rgba(30, 64, 175, 0.35)";
  };

  const uploadHeadingStyle = {
    fontWeight: 700,
    color: "#1e3a8a",
    margin: 0,
    fontFamily: "'Barlow', sans-serif",
  };

  const uploadHintStyle = {
    fontSize: 13,
    color: "#475569",
    marginTop: 6,
    fontFamily: "'Barlow', sans-serif",
  };

  const chipStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(30, 64, 175, 0.08)",
    color: "#1e3a8a",
    fontSize: 12,
    marginTop: 12,
    fontFamily: "'Barlow', sans-serif",
  };

  const iconWrapStyle = {
    width: 42,
    height: 42,
    borderRadius: 12,
    display: "grid",
    placeItems: "center",
    background: "rgba(30, 64, 175, 0.1)",
    border: "1px solid rgba(30, 64, 175, 0.2)",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    marginRight: 12,
    flex: "0 0 auto",
  };

  const safeData = useMemo(() => {
    const base = {
      serviceType: "Normal",
      profilePicture: "",
      sludiNumber: "",
      birthDate: { year: "", month: "", day: "" },
      gender: "",
      surname: "",
      otherNames: "",
      documentType: "All Countries",
      presentTravelDocNo: "",
      nmrpNo: "",
      permanentAddress: { line1: "", line2: "", city: "", district: "" },
      birthCertificateNo: "",
      birthDistrict: "",
      placeOfBirth: "",
      occupation: "",
      hasDualCitizenship: false,
      dualCitizenshipNo: "",
      foreignNationality: "",
      foreignPassportNo: "",
      mobileNumber: "",
      emailAddress: "",
      fingerprintLocation: "",
      paymentReference: "",
      paymentStatus: "",
      birthCertFront: "",
    };
    return {
      ...base,
      ...data,
      birthDate: { ...base.birthDate, ...(data?.birthDate || {}) },
      permanentAddress: {
        ...base.permanentAddress,
        ...(data?.permanentAddress || {}),
      },
    };
  }, [data]);

  const isLocked = (name) => lockedFields.includes(name);

  const setFieldGuarded = (name, value) => {
    const root = name.split(".")[0];
    if (lockedFields.includes(name) || lockedFields.includes(root)) return;
    setField(name, value);
  };

  const setBirthDateFromISO = (iso) => {
    if (isLocked("birthDate")) return;
    if (!iso) {
      setField("birthDate", { year: "", month: "", day: "" });
      return;
    }
    const [year, month, day] = iso.split("-");
    setField("birthDate", { year, month, day });
  };

  const handleFileChange = (e) => {
    const file = e.target?.files?.[0] || null;
    const name = e.target?.name;
    if (file && file.size > 1048576) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: "File size must be less than 1MB",
      }));
      setFiles((prev) => ({ ...prev, [name]: null }));
    } else {
      setFiles((prev) => ({ ...prev, [name]: file }));
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    }
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
    onFileChange(e);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    const allFieldsToTouch = [
      "sludiNumber", "birthDate", "gender", "surname", "otherNames",
      "permanentAddress.line1", "permanentAddress.city", "permanentAddress.district",
      "birthCertificateNo", "birthDistrict", "placeOfBirth", "occupation",
      "mobileNumber", "emailAddress", "birthCertFront",
    ];

    if (safeData.hasDualCitizenship) {
      allFieldsToTouch.push("dualCitizenshipNo", "foreignNationality", "foreignPassportNo");
    }

    const touchedState = {};
    allFieldsToTouch.forEach(field => {
      touchedState[field] = true;
    });
    setTouchedFields(touchedState);

    const errors = validateAllFields();

    if (errors.length > 0) {
      console.error("❌ Validation failed:", errors);
      setValidationErrors(errors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setPosting(true);
    console.group("%c[ApplicationForm] Preparing for Payment", "color:#1d4ed8;font-weight:700");

    const formData = {
      ...safeData,
      occupation: safeData.occupation === "Other" ? customOccupation : safeData.occupation,
      isRenewal,
    };

    console.log("📋 Form Data with custom occupation:", formData);
    console.log("🔎 Files:", files);

    // try {
    //   console.log("📤 Submitting to backend...");
    //   console.time("API Call Duration");

    //   const resp = await applyPassportService.submitApplication(formData, files);

    //   console.timeEnd("API Call Duration");
    //   console.log("✅ API Response:", resp);

    //   if (resp?.success && resp?.data) {
    //     console.log("✅ Submission successful, navigating to payment...");
    navigate("/passport/payment", {
      state: {
        data: formData,
        // backend: resp.data, // No backend data yet
        // applicationId: resp.data?.id,
        // applicationNumber: resp.data?.application_number,
        files: files // Pass files to handle submission later
      },
    });
    //   } else {
    //     console.error("❌ Submit failed:", resp);
    //     setSubmissionError(resp?.message || "Submission failed. Please try again.");
    //     setShowErrorDialog(true);
    //   }
    // } catch (err) {
    //   console.error("❌ Submit error:", err);
    //   setSubmissionError(err);
    //   setShowErrorDialog(true);
    // } finally {
    console.groupEnd();
    setPosting(false);
    // }
  };

  const handleViewStatus = () => {
    setShowDuplicateApplicationDialog(false);
    navigate("/application-status");
  };

  const handleRetrySubmit = () => {
    setShowErrorDialog(false);
    handleSubmit();
  };

  const dobISO =
    safeData.birthDate.year &&
      safeData.birthDate.month &&
      safeData.birthDate.day
      ? `${safeData.birthDate.year}-${String(safeData.birthDate.month).padStart(2, "0")}-${String(safeData.birthDate.day).padStart(2, "0")}`
      : "";

  const today = new Date().toISOString().split("T")[0];

  const ErrorMessage = ({ name }) => {
    if (!touchedFields[name] || !fieldErrors[name]) return null;
    return (
      <div style={{
        color: "#dc2626",
        fontSize: "0.875rem",
        marginTop: "0.25rem",
        fontFamily: "'Barlow', sans-serif",
      }}>
        {fieldErrors[name]}
      </div>
    );
  };

  const HelperText = ({ name, text }) => {
    if (touchedFields[name] && fieldErrors[name]) return null;
    if (!text) return null;
    return (
      <div style={{
        color: "#059669",
        fontSize: "0.875rem",
        marginTop: "0.25rem",
        fontFamily: "'Barlow', sans-serif",
      }}>
        {text}
      </div>
    );
  };

  const Lock = () => (
    <span style={{
      fontSize: 12,
      color: "#64748b",
      marginLeft: 6,
      fontFamily: "'Barlow', sans-serif",
    }}>🔒</span>
  );

  const ReadOnlyBox = ({ id, label, value, required }) => (
    <div>
      {label && (
        <label htmlFor={id} style={{
          display: "block",
          marginBottom: 8,
          fontWeight: 600,
          color: "#374151",
          fontFamily: "'Barlow', sans-serif",
        }}>
          {label} {required && <span style={{ color: "#dc2626" }}>*</span>}
        </label>
      )}
      <div id={id} role="textbox" aria-readonly="true" style={{
        width: "100%",
        padding: "0.75rem",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        fontSize: "1rem",
        background: "#f3f4f6",
        color: "#111827",
        fontFamily: "'Barlow', sans-serif",
      }}>
        {value || "—"}
      </div>
    </div>
  );

  const FileUploadTile = ({ name, label, file, required, hint, disabled = false }) => {
    const hasError = touchedFields[name] && fieldErrors[name];
    const isUrl = typeof file === "string" && file.startsWith("/uploads/");
    const fileUrl = isUrl
      ? `${process.env.REACT_APP_BACKEND_URL || "http://localhost:5000"}${file}`
      : null;

    if (disabled) {
      return (
        <div style={uploadTileStyle(true, hasError)}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={iconWrapStyle}>
              <span style={{ fontSize: 20 }}>🔒</span>
            </div>
            <div style={{ flex: 1 }}>
              <h4 style={uploadHeadingStyle}>
                {label} {required && <span style={{ color: "#dc2626" }}>*</span>}
              </h4>
              <p style={uploadHintStyle}>
                {hint || "Document already uploaded"}
              </p>
            </div>
          </div>

          {isUrl && (
            <div
              style={{
                ...chipStyle,
                background: "rgba(30, 64, 175, 0.08)",
                color: "#1e3a8a",
                textAlign: "center",
                cursor: "pointer",
              }}
              onClick={() => window.open(fileUrl, '_blank')}
            >
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#1e3a8a",
                  textDecoration: "underline",
                  display: "block",
                  fontFamily: "'Barlow', sans-serif",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                View uploaded file
              </a>
              <span style={{
                display: "block",
                color: "green",
                marginTop: 4,
                fontFamily: "'Barlow', sans-serif",
              }}>
                Already uploaded
              </span>
            </div>
          )}
        </div>
      );
    }

    return (
      <label
        htmlFor={name}
        style={uploadTileStyle(false, hasError)}
        onMouseEnter={(e) => onTileHover(e, hasError)}
        onMouseLeave={(e) => onTileLeave(e, hasError)}
      >
        <input
          type="file"
          id={name}
          name={name}
          accept="image/*,.pdf"
          onChange={handleFileChange}
          required={required}
          style={{ display: "none" }}
        />
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={iconWrapStyle}>
            <span style={{ fontSize: 20 }}>📄</span>
          </div>
          <div style={{ flex: 1 }}>
            <h4 style={uploadHeadingStyle}>
              {label} {required && <span style={{ color: "#dc2626" }}>*</span>}
            </h4>
            <p style={uploadHintStyle}>
              {hint || "Max size 1MB. Accepts PDF, JPG, PNG."}
            </p>
          </div>
        </div>

        {isUrl ? (
          <div style={{
            ...chipStyle,
            background: "rgba(30, 64, 175, 0.08)",
            color: "#1e3a8a",
            textAlign: "center",
          }}>
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#1e3a8a",
                textDecoration: "underline",
                fontFamily: "'Barlow', sans-serif",
              }}
            >
              View previously uploaded file
            </a>
            <span style={{
              display: "block",
              color: "green",
              marginTop: 4,
              fontFamily: "'Barlow', sans-serif",
            }}>
              ✓ Already uploaded
            </span>
          </div>
        ) : file ? (
          <div style={{
            ...chipStyle,
            background: file.size > 1048576 ? "rgba(220, 38, 38, 0.08)" : "rgba(30, 64, 175, 0.08)",
            color: file.size > 1048576 ? "#dc2626" : "#1e3a8a",
          }}>
            <span style={{ fontFamily: "'Barlow', sans-serif" }}>
              {file.size > 1048576 ? "⚠" : "📎"} {file.name}
            </span>
            <span style={{
              fontWeight: 700,
              color: file.size > 1048576 ? "#dc2626" : "#1e3a8a",
              fontFamily: "'Barlow', sans-serif",
            }}>
              ({bytes(file.size)})
            </span>
          </div>
        ) : (
          <div style={chipStyle}>
            <span style={{ fontFamily: "'Barlow', sans-serif" }}>Upload File</span>
          </div>
        )}

        <ErrorMessage name={name} />
      </label>
    );
  };

  const sectionHeaderStyle = {
    margin: "0 0 2rem 0",
    color: "#1e40af",
    fontSize: "1.75rem",
    fontWeight: "700",
    textAlign: "center",
    letterSpacing: "0.5px",
    fontFamily: "'Barlow', sans-serif",
  };

  const subSectionHeaderStyle = {
    margin: "0 0 1.5rem 0",
    color: "#1e3a8a",
    fontSize: "1.25rem",
    fontWeight: "600",
    padding: "0.75rem 1.5rem",
    backgroundColor: "rgba(30, 64, 175, 0.05)",
    borderRadius: "12px",
    borderLeft: "4px solid #1e40af",
    boxShadow: "0 2px 4px rgba(30, 64, 175, 0.1)",
    fontFamily: "'Barlow', sans-serif",
  };

  const containerStyle = {
    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
    borderRadius: "16px",
    padding: "clamp(1rem, 4vw, 2rem)",
    margin: "1rem 0",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    border: "1px solid #e5e7eb",
    fontFamily: "'Barlow', sans-serif",
  };

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "clamp(1rem, 3vw, 1.5rem)",
  };

  const cardContainerStyle = {
    marginBottom: "3rem",
    display: "flex",
    gap: "2rem",
    justifyContent: "center",
    flexWrap: "wrap",
  };

  const dualCitizenshipSectionStyle = {
    marginTop: "1rem",
    padding: "2rem",
    background: "linear-gradient(135deg, #eff6ff, #dbeafe)",
    borderRadius: "16px",
    border: "2px solid rgba(30, 64, 175, 0.2)",
    boxShadow: "0 4px 6px rgba(30, 64, 175, 0.1)",
    fontFamily: "'Barlow', sans-serif",
  };

  const dualCitizenshipHeaderStyle = {
    margin: "0 0 1.5rem 0",
    color: "#1e40af",
    fontSize: "1.1rem",
    fontWeight: "600",
    fontFamily: "'Barlow', sans-serif",
  };

  // Return statement begins here
  return (
    <div style={{
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "clamp(1rem, 4vw, 2rem)",
      background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
      minHeight: "100vh",
      fontFamily: "'Barlow', sans-serif",
    }}>
      <DuplicateApplicationDialog
        isOpen={showDuplicateApplicationDialog}
        applicationData={existingApplicationData}
        onClose={() => setShowDuplicateApplicationDialog(false)}
        onViewStatus={handleViewStatus}
      />

      <ErrorDialog
        isOpen={showErrorDialog}
        error={submissionError}
        onClose={() => setShowErrorDialog(false)}
        onRetry={handleSubmit}
        existingApplicationData={
          submissionError?.response?.data?.existingApplicationData
        }
      />

      <div style={containerStyle}>
        <h3 style={subSectionHeaderStyle}>Service Type Selection</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <label style={{ display: "flex", alignItems: "flex-start", gap: "1rem", cursor: "pointer", padding: "1rem", border: safeData.serviceType === "Urgent" ? "2px solid #1e40af" : "1px solid #d1d5db", borderRadius: "8px", background: safeData.serviceType === "Urgent" ? "rgba(30, 64, 175, 0.05)" : "white" }}>
            <input 
              type="radio" 
              name="serviceType" 
              value="Urgent" 
              checked={safeData.serviceType === "Urgent"} 
              onChange={() => setFieldGuarded("serviceType", "Urgent")}
              style={{ marginTop: "4px" }}
            />
            <div>
              <div style={{ fontWeight: 600, color: "#1e40af", fontSize: "1.1rem", fontFamily: "'Barlow', sans-serif" }}>⚡ Urgent Service</div>
              <div style={{ fontSize: "0.9rem", color: "#475569", marginTop: "4px", fontFamily: "'Barlow', sans-serif" }}>Passport Fees : Rs.15,000. Can get the passport within one day.</div>
            </div>
          </label>
          <label style={{ display: "flex", alignItems: "flex-start", gap: "1rem", cursor: "pointer", padding: "1rem", border: safeData.serviceType === "Normal" ? "2px solid #1e40af" : "1px solid #d1d5db", borderRadius: "8px", background: safeData.serviceType === "Normal" ? "rgba(30, 64, 175, 0.05)" : "white" }}>
            <input 
              type="radio" 
              name="serviceType" 
              value="Normal" 
              checked={safeData.serviceType === "Normal"} 
              onChange={() => setFieldGuarded("serviceType", "Normal")}
              style={{ marginTop: "4px" }}
            />
            <div>
              <div style={{ fontWeight: 600, color: "#1e40af", fontSize: "1.1rem", fontFamily: "'Barlow', sans-serif" }}>📅 Normal Service</div>
              <div style={{ fontSize: "0.9rem", color: "#475569", marginTop: "4px", fontFamily: "'Barlow', sans-serif" }}>Passport Fees : Rs.10,000. Standard processing time.</div>
            </div>
          </label>
        </div>
      </div>

      <div style={containerStyle}>
        <h3 style={subSectionHeaderStyle}>Personal Details</h3>
        <div style={{ display: "flex", gap: "2rem", alignItems: "flex-start", flexWrap: "wrap" }}>
          {/* Left Side: Photo */}
          <div style={{ flexShrink: 0 }}>
            {safeData.profilePicture ? (
              <img 
                src={safeData.profilePicture} 
                alt="Profile" 
                style={{ width: "clamp(96px, 30vw, 150px)", height: "clamp(96px, 30vw, 150px)", borderRadius: "12px", objectFit: "cover", boxShadow: "0 4px 6px rgba(0,0,0,0.1)", border: "3px solid white" }} 
              />
            ) : (
              <div style={{ width: "clamp(96px, 30vw, 150px)", height: "clamp(96px, 30vw, 150px)", borderRadius: "12px", backgroundColor: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", border: "3px solid white", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}>
                <span style={{ color: "#94a3b8", fontFamily: "'Barlow', sans-serif" }}>No Photo</span>
              </div>
            )}
          </div>
          
          {/* Right Side: Details */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1.5rem", flex: 1 }}>
            <div>
              <div style={{ fontWeight: 600, color: "#64748b", fontSize: "0.875rem", marginBottom: "4px", fontFamily: "'Barlow', sans-serif" }}>SLUDI Number</div>
              <div style={{ color: "#0f172a", fontSize: "1rem", fontWeight: 500, fontFamily: "'Barlow', sans-serif" }}>{safeData.sludiNumber || "—"}</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: "#64748b", fontSize: "0.875rem", marginBottom: "4px", fontFamily: "'Barlow', sans-serif" }}>Date of Birth</div>
              <div style={{ color: "#0f172a", fontSize: "1rem", fontWeight: 500, fontFamily: "'Barlow', sans-serif" }}>{dobISO || "—"}</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: "#64748b", fontSize: "0.875rem", marginBottom: "4px", fontFamily: "'Barlow', sans-serif" }}>Gender</div>
              <div style={{ color: "#0f172a", fontSize: "1rem", fontWeight: 500, fontFamily: "'Barlow', sans-serif" }}>{safeData.gender || "—"}</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: "#64748b", fontSize: "0.875rem", marginBottom: "4px", fontFamily: "'Barlow', sans-serif" }}>First Name</div>
              <div style={{ color: "#0f172a", fontSize: "1rem", fontWeight: 500, fontFamily: "'Barlow', sans-serif" }}>{safeData.surname || "—"}</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: "#64748b", fontSize: "0.875rem", marginBottom: "4px", fontFamily: "'Barlow', sans-serif" }}>Surname</div>
              <div style={{ color: "#0f172a", fontSize: "1rem", fontWeight: 500, fontFamily: "'Barlow', sans-serif" }}>{safeData.otherNames || "—"}</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: "#64748b", fontSize: "0.875rem", marginBottom: "4px", fontFamily: "'Barlow', sans-serif" }}>Mobile Number</div>
              <div style={{ color: "#0f172a", fontSize: "1rem", fontWeight: 500, fontFamily: "'Barlow', sans-serif" }}>{safeData.mobileNumber || "—"}</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: "#64748b", fontSize: "0.875rem", marginBottom: "4px", fontFamily: "'Barlow', sans-serif" }}>Email Address</div>
              <div style={{ color: "#0f172a", fontSize: "1rem", fontWeight: 500, fontFamily: "'Barlow', sans-serif" }}>{safeData.emailAddress || "—"}</div>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{ fontWeight: 600, color: "#64748b", fontSize: "0.875rem", marginBottom: "4px", fontFamily: "'Barlow', sans-serif" }}>Permanent Address</div>
              <div style={{ color: "#0f172a", fontSize: "1rem", fontWeight: 500, fontFamily: "'Barlow', sans-serif" }}>
                {[safeData.permanentAddress.line1, safeData.permanentAddress.line2, safeData.permanentAddress.city, safeData.permanentAddress.district].filter(Boolean).join(", ") || "—"}
              </div>
            </div>
          </div>
        </div>
      </div>





      <div style={containerStyle}>
        <h4 style={subSectionHeaderStyle}>Birth Information, Occupation & Relevant Documents</h4>
        <div style={gridStyle}>
          {isLocked("birthCertificateNo") ? (
            <ReadOnlyBox
              id="birthCertificateNo"
              label={<span>Birth Certificate Number <Lock /></span>}
              required
              value={safeData.birthCertificateNo || "—"}
            />
          ) : (
            <div>
              <TextField
                id="birthCertificateNo"
                name="birthCertificateNo"
                label="Birth Certificate Number"
                required
                value={safeData.birthCertificateNo}
                onChange={handleFieldChangeWithValidation}
                onBlur={() => handleFieldBlur("birthCertificateNo")}
                maxLength={FIELD_MAX_LENGTHS.birthCertificateNo}
                validationType="alphanumeric"
                error={touchedFields.birthCertificateNo && fieldErrors.birthCertificateNo}
              />
              <ErrorMessage name="birthCertificateNo" />
            </div>
          )}

          {isLocked("birthDistrict") ? (
            <ReadOnlyBox
              id="birthDistrict"
              label={<span>Birth District <Lock /></span>}
              required
              value={safeData.birthDistrict || "—"}
            />
          ) : (
            <div>
              <SelectField
                id="birthDistrict"
                name="birthDistrict"
                label="Birth District"
                required
                value={safeData.birthDistrict}
                onChange={(name, value) => {
                  handleFieldChangeWithValidation(name, value);
                }}
                onBlur={() => handleFieldBlur("birthDistrict")}
                options={BIRTH_DISTRICTS}
                error={touchedFields.birthDistrict && fieldErrors.birthDistrict}
              />
              <ErrorMessage name="birthDistrict" />
            </div>
          )}

          {isLocked("placeOfBirth") ? (
            <ReadOnlyBox
              id="placeOfBirth"
              label={<span>Place of Birth <Lock /></span>}
              required
              value={safeData.placeOfBirth || "—"}
            />
          ) : (
            <div>
              <TextField
                id="placeOfBirth"
                name="placeOfBirth"
                label="Place of Birth"
                required
                value={safeData.placeOfBirth}
                onChange={handleFieldChangeWithValidation}
                onBlur={() => handleFieldBlur("placeOfBirth")}
                maxLength={FIELD_MAX_LENGTHS.placeOfBirth}
                error={touchedFields.placeOfBirth && fieldErrors.placeOfBirth}
              />
              <ErrorMessage name="placeOfBirth" />
            </div>
          )}

          <div>
            <SelectField
              id="occupation"
              name="occupation"
              label="Occupation"
              required
              value={safeData.occupation}
              onChange={(name, value) => {
                setFieldGuarded(name, value);
                if (value !== "Other") {
                  setCustomOccupation("");
                }
                setTouchedFields(prev => ({ ...prev, occupation: true }));
                const error = validateOccupation(value, customOccupation);
                setFieldErrors(prev => ({ ...prev, occupation: error }));
              }}
              onBlur={() => handleFieldBlur("occupation")}
              options={OCCUPATIONS}
              error={touchedFields.occupation && fieldErrors.occupation}
            />
            <ErrorMessage name="occupation" />

            {safeData.occupation === "Other" && (
              <div style={{ marginTop: "1rem" }}>
                <TextField
                  id="customOccupation"
                  name="customOccupation"
                  label="Please specify your occupation"
                  required
                  value={customOccupation}
                  onChange={handleCustomOccupationChange}
                  onBlur={() => {
                    setTouchedFields(prev => ({ ...prev, occupation: true }));
                    const error = validateOccupation(safeData.occupation, customOccupation);
                    setFieldErrors(prev => ({ ...prev, occupation: error }));
                  }}
                  placeholder="e.g., Freelancer, Artist, Entrepreneur"
                  maxLength={FIELD_MAX_LENGTHS.customOccupation}
                  error={touchedFields.occupation && fieldErrors.occupation}
                />
              </div>
            )}
          </div>
        </div>
        
        <div style={{ marginTop: "2rem" }}>
          <h4 style={subSectionHeaderStyle}>Document Uploads</h4>
        <p style={{
          fontSize: "0.875rem",
          color: "#666",
          marginBottom: "0.25rem",
          fontFamily: "'Barlow', sans-serif",
        }}>
          Maximum file size: 1MB per document | File Type: pdf,png,jpg
        </p>
        <div style={uploadGridStyle}>
          <FileUploadTile
            name="birthCertFront"
            label="Birth Certificate"
            file={safeData.birthCertFront || files.birthCertFront}
            required
            hint={safeData.birthCertFront ? "Already uploaded" : "Clear scan of the front side"}
            disabled={!!safeData.birthCertFront}
          />
        </div>
        </div>
      </div>

      <div style={containerStyle}>
        <h4 style={subSectionHeaderStyle}>Type of Travel Document</h4>
        <RadioGroup
          name="documentType"
          value={safeData.documentType}
          onChange={setFieldGuarded}
          options={[
            { value: "All Countries", label: "All Countries" },
            { value: "Middle East Countries", label: "Middle East Countries" },
            { value: "Emergency Certificate", label: "Emergency Certificate" },
            { value: "Identity Certificate", label: "Identity Certificate" },
          ]}
        />

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "clamp(1rem, 3vw, 1.5rem)",
          marginTop: "clamp(1.25rem, 3.5vw, 2rem)",
        }}>
          <TextField
            id="presentTravelDocNo"
            name="presentTravelDocNo"
            label="Present Travel Document No. (If any)"
            value={safeData.presentTravelDocNo}
            onChange={handleFieldChangeWithValidation}
            maxLength={FIELD_MAX_LENGTHS.presentTravelDocNo}
          />
          <TextField
            id="nmrpNo"
            name="nmrpNo"
            label="NMRP No. (If any)"
            value={safeData.nmrpNo}
            onChange={handleFieldChangeWithValidation}
            maxLength={FIELD_MAX_LENGTHS.nmrpNo}
          />
        </div>
      </div>
      <div style={containerStyle}>
        <h4 style={subSectionHeaderStyle}>Dual Citizenship Details</h4>
        <RadioGroup
          name="hasDualCitizenship"
          label="Do you hold dual citizenship?"
          value={safeData.hasDualCitizenship ? "Yes" : "No"}
          onChange={(name, value) =>
            setFieldGuarded("hasDualCitizenship", value === "Yes")
          }
          options={[
            { value: "Yes", label: "Yes" },
            { value: "No", label: "No" },
          ]}
        />
        {safeData.hasDualCitizenship && (
          <div style={dualCitizenshipSectionStyle}>
            <h5 style={dualCitizenshipHeaderStyle}>
              Enter Dual Citizenship Information
            </h5>
            <div style={gridStyle}>
              <div>
                <TextField
                  id="dualCitizenshipNo"
                  name="dualCitizenshipNo"
                  label="Dual Citizenship Number"
                  required
                  value={safeData.dualCitizenshipNo}
                  onChange={handleFieldChangeWithValidation}
                  onBlur={() => handleFieldBlur("dualCitizenshipNo")}
                  maxLength={FIELD_MAX_LENGTHS.dualCitizenshipNo}
                  error={
                    touchedFields.dualCitizenshipNo &&
                    fieldErrors.dualCitizenshipNo
                  }
                />
                <ErrorMessage name="dualCitizenshipNo" />
              </div>
              <div>
                <TextField
                  id="foreignNationality"
                  name="foreignNationality"
                  label="Foreign Nationality"
                  required
                  value={safeData.foreignNationality}
                  onChange={handleFieldChangeWithValidation}
                  onBlur={() => handleFieldBlur("foreignNationality")}
                  maxLength={FIELD_MAX_LENGTHS.foreignNationality}
                  error={
                    touchedFields.foreignNationality &&
                    fieldErrors.foreignNationality
                  }
                />
                <ErrorMessage name="foreignNationality" />
              </div>
              <div>
                <TextField
                  id="foreignPassportNo"
                  name="foreignPassportNo"
                  label="Foreign Passport Number"
                  required
                  value={safeData.foreignPassportNo}
                  onChange={handleFieldChangeWithValidation}
                  onBlur={() => handleFieldBlur("foreignPassportNo")}
                  maxLength={FIELD_MAX_LENGTHS.foreignPassportNo}
                  error={
                    touchedFields.foreignPassportNo &&
                    fieldErrors.foreignPassportNo
                  }
                />
                <ErrorMessage name="foreignPassportNo" />
              </div>
            </div>
          </div>
        )}
      </div>





      <div style={{ marginTop: "3rem", textAlign: "right" }}>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          style={{
            padding: "1rem 3rem",
            fontSize: "1rem",
            fontWeight: 600,
            color: "white",
            background: isSubmitting
              ? "#93c5fd"
              : "linear-gradient(145deg, #1e40af, #1e3a8a)",
            border: "none",
            borderRadius: "8px",
            cursor: isSubmitting ? "not-allowed" : "pointer",
            boxShadow: isSubmitting
              ? "none"
              : "0 4px 8px rgba(30, 64, 175, 0.3)",
            transition: "all 0.2s ease",
            fontFamily: "'Barlow', sans-serif",
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 12px rgba(30, 64, 175, 0.4)";
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 8px rgba(30, 64, 175, 0.3)";
            }
          }}
        >
          {isSubmitting ? "Submitting..." : "Next to Payment"}
        </button>
      </div>
    </div>
  );
}

